<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcPaymentTransactionTypeSymbol',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    transactionType: {
      type: String,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const transactionTypes = ref({
    credit: {
      icon: 'mdi:plus-box',
      tooltip: $fn.value.pgettext('PaymentType', 'credit'),
      color: 'green',
    },
    debit: {
      icon: 'mdi:minus-box',
      tooltip: $fn.value.pgettext('PaymentType', 'debit'),
      color: 'red',
    },
    deposit: {
      icon: 'mdi:plus-box',
      tooltip: $fn.value.pgettext('PaymentType', 'deposit'),
      color: 'green',
    },
    payment: {
      icon: 'mdi:minus-box',
      tooltip: $fn.value.pgettext('PaymentType', 'payment'),
      color: 'red',
    },
    invoice_reconciliation: {
      icon: 'material-symbols:compare-arrows',
      color: 'blue',
    },
    write_off: {
      icon: 'material-symbols:arrow-right-alt',
      color: 'blue',
    },
  });
  const ux = ref([]);

  const $data = computed(() => ({
    transactionTypes: transactionTypes.value,
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'transactionTypes' }, { name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { transactionTypes, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const bindings = computed(() => {
    if (loaded.value) {
      return transactionTypes.value[props.transactionType || 'default'];
    }
    return null;
  });

  watch(
    () => props.transactionType,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('transactionType');
    }
  );

  const actions = {};

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <span
    :title="bindings.tooltip"
    class="d-inline-flex align-self-center"
    v-if="bindings"
  >
    <ItpIcon :name="bindings.icon" :color="bindings.color"> </ItpIcon>
  </span>
</template>
